import './TagInput.scss';

import ReactTagInput from '@pathofdev/react-tag-input';
import classnames from 'classnames';
import copy from 'clipboard-copy';
import { arrayOf, bool, func, string } from 'prop-types';
import React, { useEffect } from 'react';
import { GoClippy, GoKebabHorizontal } from 'react-icons/go';
import { Button, PopoverBody, Row, UncontrolledPopover } from 'reactstrap';

import FEATURE_FLAGS, { useSafeFlag } from '../../utils/flags';
import splitCombinedValues from '../../utils/split-combined-values';

function TagInput({
  name,
  onChange,
  readOnly,
  validator,
  value,
  allowCopy,
  isSkuInput,
  isCCP,
  setOverCCPLimit,
}) {
  const isDraftCampaignEnabled = useSafeFlag(FEATURE_FLAGS.DRAFT_CAMPAIGN);

  const isLimitExceeded = isDraftCampaignEnabled
    ? value.length > 5000
    : isCCP && value.length > 5000;

  useEffect(() => {
    // Only check and set limit when draft campaign is disabled (original behavior)
    if (!isDraftCampaignEnabled) {
      if (isLimitExceeded) {
        setOverCCPLimit(true);
      } else if (setOverCCPLimit) {
        setOverCCPLimit(false);
      }
    }
  }, [isLimitExceeded, setOverCCPLimit, isDraftCampaignEnabled]);

  const actionButtons = [];

  const clearAll = () => {
    onChange({ target: { name, value: [] } });
  };

  const targetName = name.split('.');
  if (!readOnly && isSkuInput) {
    actionButtons.push(
      <GoKebabHorizontal id={targetName[1]} className="actions" size={30} />,
      <UncontrolledPopover
        placement="bottom"
        target={targetName[1]}
        trigger="legacy"
      >
        <PopoverBody>
          <Row>
            <Button className="tag-input-btn" onClick={clearAll} block>
              Clear All
            </Button>
          </Row>
        </PopoverBody>
      </UncontrolledPopover>,
    );
  }

  if (!readOnly && !isSkuInput) {
    actionButtons.push(
      <Button
        key="clear"
        close
        title="Clear all"
        className="px-1"
        onClick={clearAll}
      />,
    );
  }

  if (allowCopy) {
    actionButtons.push(
      <Button
        key="clipboard-copy"
        onClick={() => copy(value)}
        className="btn-light text-muted input-group-text h-100"
      >
        <GoClippy />
      </Button>,
    );
  }

  return (
    <>
      <div
        className={classnames(
          'position-relative',
          'react-tag-input-container',
          {
            'react-tag-input-container--with-copy': allowCopy,
          },
        )}
      >
        <ReactTagInput
          tags={value}
          validator={validator}
          onChange={tags => {
            onChange({ target: { name, value: splitCombinedValues(tags) } });
          }}
          readOnly={readOnly}
          removeOnBackspace
          editable
        />
        {actionButtons.length > 0 && (
          <div className="react-tag-input__actions">{actionButtons}</div>
        )}
      </div>
      {isDraftCampaignEnabled && (
        <div
          id={`${name}-limit`}
          className={isLimitExceeded ? 'limit-exceeded' : ''}
        >
          {`${value.length}/5000`}
        </div>
      )}
      {!isDraftCampaignEnabled && (
        <div id="limit" className={isLimitExceeded ? 'limit-exceeded' : ''}>
          {isCCP && `${value.length}/5000`}
        </div>
      )}
    </>
  );
}

TagInput.propTypes = {
  name: string.isRequired,
  onChange: func,
  readOnly: bool,
  validator: func,
  value: arrayOf(string.isRequired),
  allowCopy: bool,
  isSkuInput: bool,
  isCCP: bool,
  setOverCCPLimit: func,
};

TagInput.defaultProps = {
  onChange: () => {},
  readOnly: undefined,
  validator: undefined,
  value: [],
  allowCopy: false,
  isSkuInput: false,
  isCCP: false,
  setOverCCPLimit: undefined,
};

export default TagInput;
